import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  useUser,
  fetchWithAuth,
  fetchWithAuthUpload,
} from "../../Context/UserContext";
import PageLayout from "../../components/layout/PageLayout";
import LicitacionMenu from "./LicitacionMenu";
import API_URL from "../../config";
import { LoadingOverlay } from "../../components/ui/loading-overlay";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { FileText } from "lucide-react";
import { toast } from "sonner";

const readableLabels = {
  condiciones_pago: "Condiciones de Pago",
  ciudad: "Ciudad de Origen",
  duracion_contrato: "Duración del Contrato",
};

const PreviewSection = ({ title, onEdit, children }) => (
  <Card>
    <CardContent className="space-y-2 pt-6">
      <div className="flex items-center justify-between">
        <h4 className="font-semibold text-foreground">{title}</h4>
        <Button variant="outline" size="sm" onClick={onEdit}>
          Editar
        </Button>
      </div>
      <div className="space-y-1 text-sm text-muted-foreground">{children}</div>
    </CardContent>
  </Card>
);

const PreviewTender = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useUser();
  const formData = location.state?.formData || {};
  const [loading, setLoading] = useState(false);

  const handleBack = () => {
    navigate("/create-tender-step-4", { state: { formData } });
  };

  const handleEdit = (step) => {
    navigate(step, { state: { formData } });
  };

  const handlePublish = async () => {
    setLoading(true);
    try {
      // ✅ Paso 1: Crear la licitación en la base de datos
      const tenderResponse = await fetchWithAuth(
        `${API_URL}/licit/create_tender`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            titulo: formData.titulo,
            categoria: formData.categoria,
            pais: formData.pais || "Perú",
            ciudad: formData.ciudad,
            descripcion: formData.descripcion,
            condiciones_pago: formData.condiciones_pago,
            preguntas_licitador: formData.preguntas_licitador.map((q) => ({
              question: q.question,
              type: q.type,
            })),
            duracion_contrato: formData.duracion_contrato || [],
            fecha_maxima_postulacion: formData.fecha_maxima_postulacion, // ✅ agregado aquí
            id_user: user.id_user,
            estado: "activo",
          }),
        }
      );

      if (!tenderResponse.ok) {
        if (tenderResponse.status === 403) {
          const errorData = await tenderResponse.json();
          toast.error(errorData.error || "No se pudo publicar la licitación.", {
            action: { label: "Ver planes", onClick: () => navigate("/precios") },
          });
          return;
        }
        throw new Error("Error al crear la licitación");
      }

      // ✅ Esperar confirmación del backend antes de continuar
      const tenderData = await tenderResponse.json();
      const id_tender = tenderData?.tender?.id_tender;

      if (!id_tender)
        throw new Error("No se pudo obtener el ID de la licitación");

      // ✅ Paso 2: Subir archivos a S3 y asociarlos a `id_tender`
      if (formData.archivosAdjuntos?.length > 0) {
        const uploadPromises = formData.archivosAdjuntos.map(
          async (fileObj, index) => {
            if (!(fileObj.file instanceof File)) {
              console.warn(
                `⚠️ Archivo inválido en posición ${index}:`,
                fileObj
              );
              return null;
            }

            const fileData = new FormData();
            fileData.append("file", fileObj.file);
            fileData.append("id_tender", id_tender);
            fileData.append("file_name", fileObj.name); // ✅ Mandar el nombre del archivo

            // 🛠️ Debug: Verificar el contenido de FormData antes de enviarlo
            console.log(`📂 Enviando archivo: ${fileObj.name}`);
            for (let pair of fileData.entries()) {
              console.log(`🔍 ${pair[0]}:`, pair[1]);
            }

            const response = await fetchWithAuthUpload(
              `${API_URL}/upload/upload-file`,
              {
                method: "POST",
                body: fileData,
              }
            );

            if (!response.ok) {
              console.error(
                "❌ Error en la subida del archivo:",
                await response.text()
              );
              throw new Error("Error al subir archivo");
            }

            return response.json();
          }
        );

        await Promise.all(uploadPromises);
      }

      if (tenderData.requiere_pago) {
        toast.message("Licitación creada, pendiente de pago");
        navigate(`/licitaciones/${id_tender}/comprobante-pago`, {
          state: { instruccionesPago: tenderData.instrucciones_pago },
        });
        return;
      }

      toast.success("Licitación publicada exitosamente");
      navigate("/mis-licitaciones");
    } catch (error) {
      console.error("Error al publicar la licitación:", error);
      toast.error("Hubo un error al intentar publicar la licitación.");
    } finally {
      setLoading(false);
    }
  };

  const formattedInfo = Object.entries(formData.informacion_licitador || {})
    .filter(([_, value]) => value)
    .map(([key, value]) => ({
      label: readableLabels[key] || key,
      value: typeof value === "boolean" ? (value ? "Sí" : "No") : value,
    }));

  // Formatear la fecha final en formato legible
  const formatFechaFinal = (fechaISO) => {
    if (!fechaISO) return "No especificado";

    const fecha = new Date(fechaISO);
    return fecha.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  // Calcular diferencia de tiempo
  const calcularDiferenciaTiempo = (fechaISO) => {
    if (!fechaISO) return "No especificado";

    const fechaFinal = new Date(fechaISO);
    const ahora = new Date();
    const diferenciaMs = fechaFinal - ahora;

    if (diferenciaMs < 0) return "Fecha ya pasada";

    const dias = Math.floor(diferenciaMs / (1000 * 60 * 60 * 24));
    const meses = Math.floor(dias / 30);
    const diasRestantes = dias % 30;

    if (meses > 0 && diasRestantes > 0) {
      return `${meses} meses - ${diasRestantes} días`;
    } else if (meses > 0) {
      return `${meses} meses`;
    } else {
      return `${dias} días`;
    }
  };

  const fechaFinalTexto = formatFechaFinal(formData.fecha_maxima_postulacion);
  const tiempoRestanteTexto = calcularDiferenciaTiempo(
    formData.fecha_maxima_postulacion
  );

  return (
    <PageLayout>
      {loading && <LoadingOverlay />}

      <div className="mx-auto max-w-3xl px-4 py-8 sm:px-6">
        <LicitacionMenu currentStep={5} />

        <h3 className="text-center text-xl font-semibold text-foreground">Vista Previa de la Licitación</h3>

        <div className="mt-6 space-y-4">
          <PreviewSection title="Información General" onEdit={() => handleEdit("/create-tender-step-1")}>
            <p>
              <strong className="text-foreground">Categoría:</strong> {formData.categoria}
            </p>
            <p>
              <strong className="text-foreground">Título:</strong> {formData.titulo}
            </p>
            <p>
              <strong className="text-foreground">Descripción:</strong>{" "}
              <span
                dangerouslySetInnerHTML={{
                  __html: (formData.descripcion || "").replace(/\n/g, "<br>"),
                }}
              />
            </p>

            {formData.archivosAdjuntos?.length > 0 && (
              <div>
                <strong className="text-foreground">Archivos Adjuntos:</strong>
                <ul className="mt-1 space-y-1">
                  {formData.archivosAdjuntos.map((fileObj, index) => {
                    const file = fileObj.file || fileObj; // Si `fileObj` tiene `file`, úsalo; si no, es un archivo normal
                    const fileName =
                      fileObj.name || file.name || `Archivo ${index + 1}`;

                    // Generar URL para descarga solo si es un objeto `File`
                    const fileUrl =
                      file instanceof File
                        ? URL.createObjectURL(file)
                        : file.file_path;

                    return (
                      <li key={index}>
                        {fileUrl ? (
                          <a
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={fileName}
                            className="flex items-center gap-1.5 text-primary hover:underline"
                          >
                            <FileText className="h-4 w-4" /> {index + 1}. {fileName}
                          </a>
                        ) : (
                          <span>No disponible</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </PreviewSection>

          <PreviewSection title="Información Adicional" onEdit={() => handleEdit("/create-tender-step-3")}>
            <p>
              <strong className="text-foreground">Fecha de cierre de la licitación:</strong> {fechaFinalTexto}{" "}
              <span className="italic">({tiempoRestanteTexto})</span>
            </p>
            <p>
              <strong className="text-foreground">Ciudades donde se dará el servicio:</strong>{" "}
              {formData.ciudad?.join(", ") || "No especificado"}
            </p>
            <p>
              <strong className="text-foreground">Condiciones de Pago:</strong>{" "}
              {formData.condiciones_pago?.join(", ") || "No especificado"}
            </p>
            <p>
              <strong className="text-foreground">Duración del Contrato:</strong>{" "}
              {formData.duracion_contrato?.length > 0
                ? formData.duracion_contrato.join(", ")
                : "No especificado"}
            </p>
            {formattedInfo.length > 0 &&
              formattedInfo.map(({ label, value }, index) => (
                <p key={index}>
                  <strong className="text-foreground">{label}:</strong> {value}
                </p>
              ))}
          </PreviewSection>

          {formData.preguntas_licitador?.length > 0 && (
            <PreviewSection
              title="Preguntas para el Postulante"
              onEdit={() => handleEdit("/create-tender-step-4")}
            >
              <ul className="space-y-1">
                {formData.preguntas_licitador.map((q, index) => (
                  <li key={index}>
                    {index + 1}. {q.question} <span className="text-xs">({q.type})</span>
                  </li>
                ))}
              </ul>
            </PreviewSection>
          )}

          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={handleBack}>
              Regresar
            </Button>
            <Button type="button" onClick={handlePublish}>
              Publicar
            </Button>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PreviewTender;
